import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { formatInteger } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import AdminFormHeader from "@/components/admin/AdminFormHeader";

type Waitlist = {
  id: string;
  retreatId: string;
  retreatTitle: string | null;
  name: string;
  slug: string;
  klaviyoListId: string;
  status: "active" | "disabled";
  active: boolean;
  signupCount: number;
};

type WaitlistSortKey = "name" | "retreat" | "list" | "signups" | "status";

type RetreatOption = { id: string; slug: string; title: string };

type FormState = {
  retreatId: string;
  name: string;
  klaviyoListId: string;
  active: boolean;
};

const empty: FormState = {
  retreatId: "",
  name: "",
  klaviyoListId: "",
  active: true,
};

function toForm(w: Waitlist): FormState {
  return {
    retreatId: w.retreatId,
    name: w.name,
    klaviyoListId: w.klaviyoListId,
    active: w.active,
  };
}

function waitlistSearchText(waitlist: Waitlist) {
  return [
    waitlist.name,
    waitlist.slug,
    waitlist.retreatTitle,
    waitlist.klaviyoListId,
    waitlist.status,
    formatInteger(waitlist.signupCount),
  ]
    .filter(Boolean)
    .join(" ");
}

const waitlistSorters: Record<WaitlistSortKey, (row: Waitlist) => string | number> = {
  name: (row) => row.name,
  retreat: (row) => row.retreatTitle ?? "",
  list: (row) => row.klaviyoListId,
  signups: (row) => row.signupCount,
  status: (row) => row.status,
};

export default function AdminWaitlists() {
  const [list, setList] = useState<Waitlist[]>([]);
  const [retreats, setRetreats] = useState<RetreatOption[]>([]);
  const [editing, setEditing] = useState<Waitlist | "new" | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  async function refresh() {
    const r = await api.get<{ waitlists: Waitlist[] }>("/api/admin/waitlists");
    setList(r.waitlists);
  }

  useEffect(() => {
    Promise.all([
      refresh(),
      api
        .get<{ retreats: RetreatOption[] }>("/api/admin/waitlists/retreats-options")
        .then((r) => setRetreats(r.retreats)),
    ]).finally(() => setLoaded(true));
  }, []);

  function startNew() {
    setForm({ ...empty, retreatId: retreats[0]?.id ?? "" });
    setEditing("new");
    setErr(null);
  }

  function startEdit(w: Waitlist) {
    setForm(toForm(w));
    setEditing(w);
    setErr(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const payload = {
      retreatId: form.retreatId,
      name: form.name,
      klaviyoListId: form.klaviyoListId,
      active: form.active,
    };
    try {
      if (editing === "new") await api.post("/api/admin/waitlists", payload);
      else if (editing) await api.put(`/api/admin/waitlists/${editing.id}`, payload);
      await refresh();
      setEditing(null);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(w: Waitlist) {
    if (!confirm(`Delete waitlist "${w.name}"? This will also delete all signups. This cannot be undone.`)) return;
    await api.del(`/api/admin/waitlists/${w.id}`);
    await refresh();
  }

  const filters = useMemo(
    () => [(row: Waitlist) => statusFilter === "all" || row.status === statusFilter],
    [statusFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Waitlist, WaitlistSortKey>({
    rows: list,
    search,
    initialSort: { key: "name", direction: "asc" },
    sorters: waitlistSorters,
    searchText: waitlistSearchText,
    filters,
  });

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Retreat Waitlists</h1>
        {!editing && (
          <button type="button" className="btn-woven" onClick={startNew}>
            + New waitlist
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={submit} className="paper-card">
          <AdminFormHeader
            title={editing === "new" ? "New waitlist" : editing.name}
            eyebrow={editing === "new" ? "Create waitlist" : "Edit waitlist"}
            backLabel="Back to waitlists"
            onBack={() => setEditing(null)}
          />
          <div className="grid2">
            <label className="fld">
              <span>Retreat</span>
              <select
                value={form.retreatId}
                onChange={(e) => setForm({ ...form, retreatId: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {retreats.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label="Name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
            />
          </div>
          <Field
            label="Klaviyo List ID"
            value={form.klaviyoListId}
            onChange={(v) => setForm({ ...form, klaviyoListId: v })}
            required
          />
          <label className="fld checkbox">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <span style={{ marginLeft: 8 }}>Active</span>
          </label>
          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn-woven" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </form>
      ) : !loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search waitlists, retreats, Klaviyo lists..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: "All statuses" },
                { value: "active", label: "Active" },
                { value: "disabled", label: "Disabled" },
              ]}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No waitlists yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No waitlists match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={sort} onSort={requestSort} />
                  <SortableTh label="Retreat" sortKey="retreat" sort={sort} onSort={requestSort} />
                  <SortableTh label="Klaviyo List ID" sortKey="list" sort={sort} onSort={requestSort} />
                  <SortableTh label="Signups" sortKey="signups" sort={sort} onSort={requestSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={requestSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((w) => (
                  <tr key={w.id}>
                    <td>
                      <strong>{w.name}</strong>
                      <span className="slug">{w.slug}</span>
                    </td>
                    <td>{w.retreatTitle ?? "-"}</td>
                    <td>
                      <span className="slug">{w.klaviyoListId}</span>
                    </td>
                    <td>{formatInteger(w.signupCount)}</td>
                    <td>{w.status}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="btn-ghost sm" onClick={() => startEdit(w)}>
                        Edit
                      </button>
                      <button type="button" className="btn-danger sm" onClick={() => remove(w)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .fld.checkbox { display: flex; align-items: center; padding-top: 0.4rem; }
  .fld.checkbox input { accent-color: ${colors.berryRose}; }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
`;
